import './styles/Module.scss';

import { useMutation } from '@apollo/client';
import { Grid } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { Module } from '@thro-types/modules-selections';

import { Formik } from 'formik';
import { Gateway } from 'index';
import { omit, omitBy } from 'lodash';
import omitDeep from 'omit-deep-lodash';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import * as yup from 'yup';

import CREATE_MUTATION from 'gql/mutations/ModulesService/addDefaultModule.gql';
import UPDATE_MUTATION from 'gql/mutations/ModulesService/updateModule.gql';
import DEFAULT_MODULES_QUERY from 'gql/queries/ModulesService/getDefaultModules.gql';
import QUERY from 'gql/queries/ModulesService/getModuleById.gql';

import { invalidateDefaultModulesQuery, invalidateModulesQuery } from 'api/hooks/modules';
import MultistepForm from 'app/components/MultistepForm';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { Course } from 'types/AppTypes';
import { excludeCoursesFromAllCourses } from 'utils/excludeCoursesHelper';

import { SubjectType } from '../../ModuleSelections/models';
import GeneralInfo from './components/GeneralInfo';
import ModulePreview from './components/ModulePreview';
import PeriodsAndParticipants from './components/PeriodsAndParticipants';
import PrioritizationAndApproval from './components/PrioritizationAndApproval';
import { moduleInitialValues, moduleValidationSchema } from './validation';

interface Props {
  closeHandler: Function;
  subjectType: SubjectType;
  module?: Module;
  setDisabledCancel?: Function;
  courses: Course[];
}

export default function ModuleModal(props: Props) {
  const { t } = useTranslation();
  const [submitting, setSubmitting] = useState(false);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const [createModule] = useMutation(CREATE_MUTATION, {
    context: { clientName: 'federation' }
  });
  const [updateModule] = useMutation(UPDATE_MUTATION, {
    context: { clientName: 'federation' }
  });
  const steps = [
    t(translations.pages.modules.steps.GeneralInfo()),
    t(translations.pages.modules.steps.Participants()),
    t(translations.pages.modules.steps.PrioritizationAndApproval()),
    t(translations.pages.modules.steps.ReviewAndCreate())
  ];
  const handleSaveClick = async values => {
    if (submitting) {
      return;
    }
    setSubmitting(true);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(true);
    }
    const omitFields = ['isDefault', 'lecturersUsers'];
    let saveBody: any = {
      ...omit(values, omitFields)
    };
    if (values.attachments) {
      const formData = new FormData();
      values.attachments.forEach(p => {
        if (p instanceof Blob) {
          formData.append('files', p);
        }
      });
      let savedAttachments;
      if (formData.has('files')) {
        try {
          const { data } = await Gateway.post('/upload/modules/attachments', formData);
          savedAttachments = data;
        } catch (e) {
          dispatch(
            AlertActions.error(t(translations.pages.modules.alerts.failedAttachmentUpload()))
          );
          setSubmitting(false);
          if (props.setDisabledCancel) {
            props.setDisabledCancel(false);
          }
          return;
        }

        saveBody.attachments = [
          ...saveBody.attachments.filter(
            existingAttachment =>
              savedAttachments.findIndex(
                newAttachment => newAttachment.originalname === existingAttachment.originalname
              ) === -1 && existingAttachment.originalname
          ),
          ...savedAttachments.map(attachment => ({
            name: attachment.name,
            originalname: attachment.originalname
          }))
        ];
      }
    }
    try {
      saveBody.participantsConfigurations =
        values?.participantsConfigurations?.map(participantCfg => {
          if (participantCfg.excludeCourses && participantCfg.excludedCourseOfStudyIds.length > 0)
            participantCfg.courseOfStudyIds = excludeCoursesFromAllCourses(
              participantCfg.excludedCourseOfStudyIds,
              props?.courses
            );
          delete participantCfg['excludeCourses'];
          delete participantCfg['excludedCourseOfStudyIds'];
          if (participantCfg.custom) {
            return {
              ...participantCfg,
              prioritization: (participantCfg?.prioritization as Array<any>).map((prio, index) => ({
                priority: index + 1,
                semester: prio.semester
              })),
              courseOfStudyIds: participantCfg.isGlobal ? [] : participantCfg.courseOfStudyIds
            };
          }
          return {
            ...participantCfg,
            prioritization: (participantCfg?.prioritization as Array<any>).map(prio =>
              omit(prio, '__typename')
            ),
            courseOfStudyIds: participantCfg.isGlobal ? [] : participantCfg.courseOfStudyIds
          };
        }) || [];
    } catch (error) {
      dispatch(AlertActions.error(t(translations.pages.modules.alerts.failedCreate())));
    }
    if (values.id) {
      try {
        await updateModule({
          variables: {
            id: parseInt(values.id, 10),
            data: omitDeep(
              omitBy(saveBody, (v, k) => v === undefined || k === 'id'),
              '__typename'
            )
          },
          context: { clientName: 'federation' },
          refetchQueries: [
            {
              query: QUERY,
              variables: { id: parseInt(values.id, 10) },
              context: { clientName: 'federation' }
            },
            {
              query: DEFAULT_MODULES_QUERY,
              variables: { subjectType: props.subjectType },
              context: { clientName: 'federation' }
            }
          ]
        });
        invalidateDefaultModulesQuery(queryClient);
        invalidateModulesQuery(queryClient);
        props.closeHandler();
        dispatch(AlertActions.success(t(translations.pages.modules.alerts.successfulUpdate())));
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.modules.alerts.failedUpdate())));
      }
    } else {
      try {
        await createModule({
          variables: {
            data: omitBy(saveBody, v => v === null)
          },
          refetchQueries: ['GetDefaultModule']
        });
        props.closeHandler();
        dispatch(AlertActions.success(t(translations.pages.modules.alerts.successfulCreate())));
      } catch (err) {
        dispatch(AlertActions.error(t(translations.pages.modules.alerts.failedCreate())));
      }
    }
    setSubmitting(false);
    if (props.setDisabledCancel) {
      props.setDisabledCancel(false);
    }
  };

  const ModuleStep = ({ step }) => {
    switch (step) {
      case 0:
        return <GeneralInfo subjectType={props.subjectType} />;
      case 1:
        return <PeriodsAndParticipants />;
      case 2:
        return <PrioritizationAndApproval subjectType={props.subjectType} />;
      case 3:
        return <ModulePreview subjectType={props.subjectType} />;
      default:
        return null;
    }
  };

  return (
    <Grid item className="moduleModal">
      <Formik
        isInitialValid={!!props.module && !!props.subjectType}
        initialValues={moduleInitialValues(props.module, props.subjectType)}
        validationSchema={yup.object().shape(moduleValidationSchema(t))}
        onSubmit={values => {
          // We need the manual yup cast because formik doesnt do that automatically but
          // we need it for the yup transform() to take effect
          handleSaveClick(yup.object().shape(moduleValidationSchema(t)).cast(values));
        }}
        validateOnChange={false}
      >
        <MultistepForm
          submitting={submitting}
          steps={steps}
          onCancel={props.closeHandler}
          isStepperClickable={true}
        >
          {step => (
            <Grid container className="ModuleModal__body">
              <ModuleStep step={step} />
            </Grid>
          )}
        </MultistepForm>
      </Formik>
    </Grid>
  );
}
